/**
 * AI video generation, provider-independent part (see ai.ts for banners).
 *
 * From a one-line brief the model picks a motion reel blueprint (launch film, showreel,
 * manifesto, data story, brand film), a palette, a font, a photo and a music style, and writes
 * the short copy. The reel blueprints build the scenes, motion graphics and sound effects, so
 * the answer stays small and every result is a valid, editable video template.
 */
import { z } from 'zod';
import { PHOTOS, type PhotoId } from './gallery/photos';
import type { GalleryLocale } from './gallery';
import { applyAutoSfx } from './motion/auto-sfx';
import { MUSIC_STYLE_INFO, MUSIC_STYLES, type MusicStyle } from './motion/music';
import type { TemplateDoc } from './template';
import { VideoBuilder } from './video-gallery/kit';
import {
  REEL_FONTS,
  REEL_PALETTE_IDS,
  REEL_PALETTES,
  REEL_STYLE_IDS,
  REEL_STYLES,
  type ReelCopy,
  type ReelPaletteId,
  type ReelStyleId,
} from './video-gallery/reel';

const PHOTO_IDS = Object.keys(PHOTOS) as [PhotoId, ...PhotoId[]];

/** Character limits asked of the model (and maxLength in the answer schema). */
export const AI_VIDEO_LIMITS = {
  name: 40,
  brand: 20,
  hud: 26,
  prompt: 44,
  word: 14,
  headline: 40,
  tagline: 56,
  cta: 24,
  topic: 22,
  label: 24,
  code: 44,
} as const;

const s = (max: number) => z.string().trim().max(max);

export const aiVideoSpecSchema = z.object({
  name: s(80),
  style: z.enum(REEL_STYLE_IDS),
  palette: z.enum(REEL_PALETTE_IDS as [ReelPaletteId, ...ReelPaletteId[]]),
  font: z.string().max(64).optional(),
  photo: z.string().max(40).optional(),
  music: z.enum(MUSIC_STYLES).optional(),
  copy: z.object({
    brand: s(30),
    hud: s(40),
    prompt: s(60),
    words: z.array(s(20)).min(2).max(5),
    headline: s(60),
    tagline: s(80),
    cta: s(32),
    topics: z.array(s(30)).min(1).max(6),
    stat: z.object({
      value: z.number().min(0).max(999_999),
      prefix: s(4).optional(),
      suffix: s(6).optional(),
      label: s(30),
    }),
    code: z.array(s(60)).max(8),
  }),
});
export type AiVideoSpec = z.infer<typeof aiVideoSpecSchema>;

/** Shorten at a word boundary; never mid-word. */
const clip = (v: unknown, max: number): string | undefined => {
  if (typeof v !== 'string' && typeof v !== 'number') return undefined;
  const str = String(v).replace(/\s+/g, ' ').trim();
  if (!str) return undefined;
  if (str.length <= max) return str;
  const head = str.slice(0, max);
  const space = head.lastIndexOf(' ');
  return (
    (space > max * 0.4 ? head.slice(0, space) : head.slice(0, max - 1)).replace(
      /[\s,;:–—-]+$/,
      '',
    ) + '…'
  );
};

const list = (v: unknown, max: number, len: number) =>
  (Array.isArray(v) ? v : typeof v === 'string' ? v.split(/\n|,|\s\/\s/) : [])
    .map((x) => clip(x, len))
    .filter((x): x is string => Boolean(x))
    .slice(0, max);

/** Bring one model variant into shape (lenient: fills gaps, shortens, drops unknowns). */
function normalizeVariant(raw: unknown): AiVideoSpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  const c = (v.copy && typeof v.copy === 'object' ? v.copy : v) as Record<string, unknown>;
  const headline = clip(c.headline ?? c.title, 60);
  const brand = clip(c.brand, 30) ?? clip(v.name, 30) ?? clip(headline?.split(' ')[0], 30);
  if (!headline || !brand) return null;
  let words = list(c.words, 5, 20);
  if (words.length < 2)
    words = headline
      .split(' ')
      .slice(0, 3)
      .map((w) => w.replace(/[.,!?]+$/, '') + '.');
  if (words.length < 2) words = [...words, brand];
  const topics = list(c.topics, 6, 30);
  const st = (c.stat && typeof c.stat === 'object' ? c.stat : {}) as Record<string, unknown>;
  const value = Number(st.value);
  const spec = {
    name: clip(v.name, 80) ?? headline,
    style: (REEL_STYLE_IDS as readonly string[]).includes(String(v.style)) ? v.style : 'launch',
    palette: (REEL_PALETTE_IDS as string[]).includes(String(v.palette)) ? v.palette : 'ember',
    font: typeof v.font === 'string' && REEL_FONTS.includes(v.font) ? v.font : undefined,
    photo:
      typeof v.photo === 'string' && (PHOTO_IDS as string[]).includes(v.photo)
        ? v.photo
        : undefined,
    music: (MUSIC_STYLES as readonly string[]).includes(String(v.music)) ? v.music : undefined,
    copy: {
      brand,
      hud: clip(c.hud, 40) ?? brand.toUpperCase(),
      prompt: clip(c.prompt, 60) ?? headline.toLowerCase(),
      words,
      headline,
      tagline: clip(c.tagline, 80) ?? headline,
      cta: clip(c.cta, 32) ?? brand,
      topics: topics.length ? topics : [brand],
      stat: {
        value: Number.isFinite(value) ? Math.max(0, Math.min(999_999, Math.round(value))) : 0,
        prefix: clip(st.prefix, 4),
        suffix: clip(st.suffix, 6),
        label: clip(st.label, 30) ?? '',
      },
      code: list(c.code, 8, 60),
    },
  };
  const parsed = aiVideoSpecSchema.safeParse(spec);
  return parsed.success ? parsed.data : null;
}

export function parseAiVideos(raw: unknown): AiVideoSpec[] {
  const items = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { variants?: unknown }).variants)
      ? (raw as { variants: unknown[] }).variants
      : [];
  return items
    .slice(0, 6)
    .map(normalizeVariant)
    .filter((v): v is AiVideoSpec => v !== null);
}

/** Turn a validated spec into a video template of the requested size and language. */
export function buildVideoFromSpec(
  spec: AiVideoSpec,
  width: number,
  height: number,
  locale: GalleryLocale,
): TemplateDoc {
  const style = REEL_STYLES[spec.style];
  const base = REEL_PALETTES[spec.palette];
  const palette = {
    ...base,
    ...(spec.font && REEL_FONTS.includes(spec.font) ? { font: spec.font } : {}),
  };
  const copy: ReelCopy = {
    ...spec.copy,
    code: spec.copy.code.length
      ? spec.copy.code
      : [`// ${spec.copy.brand}`, `launch('${spec.copy.brand}')`],
    photo:
      spec.photo && (PHOTO_IDS as string[]).includes(spec.photo)
        ? (spec.photo as PhotoId)
        : undefined,
  };
  const b = new VideoBuilder({ width, height, locale }, style.scenes(copy));
  const background = style.build(b, copy, palette);
  return applyAutoSfx(b.build(background));
}

const LANGUAGE: Record<GalleryLocale, string> = {
  en: 'English',
  uk: 'Ukrainian',
  pl: 'Polish',
  ro: 'Romanian',
  es: 'Spanish',
  ru: 'Russian',
};

function paletteLine(id: ReelPaletteId): string {
  const p = REEL_PALETTES[id];
  return `- ${id}: background ${p.bg}, accent ${p.accent}, second ${p.accent2}, pop ${p.pop}, paper ${p.paper}; font ${p.font}`;
}

/** Static system instructions (cached as a prompt prefix), generated from the code. */
export function aiVideoSystemPrompt(): string {
  return [
    'You are a motion designer and copywriter. From a short brief you plan short social videos.',
    'You do not animate: you pick one of the existing reel blueprints, a palette, a font, a photo',
    'and a music style, and write very short copy. The editor builds the video from your choices.',
    '',
    'Blueprints ("style"):',
    ...REEL_STYLE_IDS.map((id) => `- ${id}: ${REEL_STYLES[id].about}`),
    '',
    'Palettes:',
    ...REEL_PALETTE_IDS.map(paletteLine),
    '',
    `Display fonts (optional, otherwise the palette font): ${REEL_FONTS.join(', ')}.`,
    'Photos (field "photo", used by the brand blueprint; "none" when nothing fits):',
    ...Object.entries(PHOTOS).map(([id, p]) => `- ${id}: ${p.alt}`),
    `Music styles (field "music"): ${MUSIC_STYLES.map((m) => `${m} (${MUSIC_STYLE_INFO[m].bpm} BPM)`).join(', ')}.`,
    '',
    'Copy fields:',
    '- brand: the brand or product name from the brief (invent a short fitting one only if none is given).',
    '- hud: a tiny viewfinder label in capitals, like "LAUNCH FILM · 2026".',
    '- prompt: what someone would type in a terminal or a prompt box to get this, lowercase.',
    '- words: 2-4 punchy one-word beats ending with a period, like "Prompt." "Motion." "Ship."',
    '- headline: the main line, 2-6 words. tagline: one short sentence. cta: 1-3 words (a verb).',
    '- topics: 3-5 short features, topics or numbers ("NPS +71").',
    '- stat: one number from the brief with prefix/suffix and a 1-3 word label (value 0 if none).',
    '- code: for launch films of software only, 3-6 short lines of plausible code; otherwise [].',
    '',
    'Rules:',
    '- Make every variant clearly different: another blueprint and palette, another angle in the copy.',
    '- Fit the blueprint to the brief: software or product → launch, a person or studio → showreel,',
    '  a bold idea or campaign → manifesto, results or numbers → data, travel/outdoor/lifestyle → brand.',
    `- Hard length limits in characters: ${Object.entries(AI_VIDEO_LIMITS)
      .map(([k, v]) => `${k} ${v}`)
      .join(', ')}. Shorter is better: these are animated in big type.`,
    '- Use only facts from the brief (prices, dates, numbers, names); never invent numbers.',
    '- Write all copy (except code and brand names) in the requested language.',
  ].join('\n');
}

export function aiVideoUserPrompt(input: {
  brief: string;
  width: number;
  height: number;
  locale: GalleryLocale;
  count: number;
}): string {
  const shape =
    input.height > input.width * 1.5
      ? 'vertical 9:16 (Reels, TikTok, Shorts)'
      : input.height > input.width * 1.1
        ? 'portrait 4:5'
        : input.width > input.height * 1.2
          ? 'landscape 16:9'
          : 'square';
  return [
    `Video size: ${input.width}x${input.height} px (${shape}).`,
    `Language of the copy: ${LANGUAGE[input.locale]}.`,
    `Give ${input.count} variants.`,
    '',
    'Brief from the user:',
    input.brief,
  ].join('\n');
}

/** JSON schema of the answer (OpenAPI subset for structured output). */
export function aiVideoJsonSchema(count: number) {
  const str = (max: number, description?: string) => ({
    type: 'STRING',
    maxLength: max,
    ...(description ? { description } : {}),
  });
  const L = AI_VIDEO_LIMITS;
  return {
    type: 'OBJECT',
    properties: {
      variants: {
        type: 'ARRAY',
        minItems: count,
        maxItems: count,
        items: {
          type: 'OBJECT',
          properties: {
            name: str(L.name),
            style: { type: 'STRING', enum: [...REEL_STYLE_IDS] },
            palette: { type: 'STRING', enum: REEL_PALETTE_IDS },
            font: { type: 'STRING', enum: REEL_FONTS },
            photo: { type: 'STRING', enum: [...PHOTO_IDS, 'none'] },
            music: { type: 'STRING', enum: [...MUSIC_STYLES] },
            copy: {
              type: 'OBJECT',
              properties: {
                brand: str(L.brand),
                hud: str(L.hud),
                prompt: str(L.prompt),
                words: { type: 'ARRAY', items: str(L.word), minItems: 2, maxItems: 4 },
                headline: str(L.headline, '2-6 words'),
                tagline: str(L.tagline),
                cta: str(L.cta, '1-3 words'),
                topics: { type: 'ARRAY', items: str(L.topic), minItems: 3, maxItems: 5 },
                stat: {
                  type: 'OBJECT',
                  properties: {
                    value: { type: 'NUMBER' },
                    prefix: str(4),
                    suffix: str(6),
                    label: str(L.label),
                  },
                  required: ['value', 'label'],
                },
                code: { type: 'ARRAY', items: str(L.code), maxItems: 6 },
              },
              required: [
                'brand',
                'hud',
                'prompt',
                'words',
                'headline',
                'tagline',
                'cta',
                'topics',
                'stat',
                'code',
              ],
            },
          },
          required: ['name', 'style', 'palette', 'photo', 'music', 'copy'],
        },
      },
    },
    required: ['variants'],
  };
}

/** Offline stand-in for a model (tests, development without a key): the brief in every blueprint. */
export function mockVideoSpecs(brief: string, count: number): AiVideoSpec[] {
  const clean = brief.replace(/\s+/g, ' ').trim() || 'New launch';
  const words = clean.split(' ').filter(Boolean);
  const brand =
    (words.find((w) => /^\p{Lu}/u.test(w)) ?? words[0] ?? 'Nova')
      .replace(/[^\p{L}\p{N}-]/gu, '')
      .slice(0, 20) || 'Nova';
  const number = Number(clean.match(/\d+/)?.[0] ?? 0);
  const plan: Array<[ReelStyleId, ReelPaletteId, MusicStyle]> = [
    ['launch', 'ember', 'pulse'],
    ['manifesto', 'acid', 'drive'],
    ['data', 'cobalt', 'hiphop'],
    ['showreel', 'signal', 'tension'],
  ];
  return plan.slice(0, Math.max(1, count)).map(([style, palette, music], i) => {
    const raw = {
      name: `${brand} · ${style}`,
      style,
      palette,
      music,
      photo: 'mountain-lake',
      copy: {
        brand,
        hud: `${brand.toUpperCase()} · 2026`,
        prompt: clean.toLowerCase().slice(0, 44),
        words: words.slice(0, 3).map((w) => `${w.replace(/[.,!?]+$/, '')}.`),
        headline: words.slice(0, 5).join(' ').slice(0, 40),
        tagline: clean.slice(0, 56),
        cta: i % 2 ? 'Try it' : 'Get started',
        topics: words.slice(0, 4),
        stat: { value: number, suffix: number ? '%' : '', label: number ? 'growth' : '' },
        code: style === 'launch' ? [`launch('${brand}')`] : [],
      },
    };
    return normalizeVariant(raw)!;
  });
}
